// Patient.js
// This is the Patient Details view.

import React, { useCallback, useEffect, useReducer } from "react";
import { ActivityIndicator, View, Text } from "react-native";
import { FontAwesomeIcon } from "@fortawesome/react-native-fontawesome";
import { faNoteSticky } from "@fortawesome/free-regular-svg-icons";
import { faPaw, faArrowsRotate, faHeartPulse } from "@fortawesome/free-solid-svg-icons";

import Anchor from "components/Partials/Anchor";
import { stateReducer } from "components/helpers/stateReducer";
import { openSideScreen } from "components/Views/SideScreens/SideScreenLoader";

import { PatientProfile, UpcomingAppointments, PatientVaccinations, PatientReminders, PatientPrescriptions } from "components/Views/SideScreens/Partials";


const PatientDetails = ({client_id, patient_id}) => {
  const _theme = window.main.theme;

  if (!Number(client_id) || !Number(patient_id)) {
    return (
      <View id="patient_details_invalid" style={{..._theme.tiles.outerContainer,alignItems:"center",paddingTop:50}}>
        <Text style={{..._theme.tiles.fieldRowValue,color:_theme.colors.mode.placeholderTextColor,fontStyle:"italic",fontSize:18,lineHeight:22}}>Patient Details: Invalid patient.</Text>
      </View>
    );
  }

  const [patientViewState, patientViewSetState] = useReducer(stateReducer, {
    patientDetails: {},
    patientLoading: true,  // ⬅ Default TRUE for iniital 'loading' state.
    patientServices: [],
    patientServicesLoading: true,
    patientVisits: [],
    patientVisitsLoading: true,
  });

  const initialDataLoad = useCallback(async () => {
    const _stateProps = [{key:"patientLoading", type:"boolean", patientLoading:false }];
    try {
      // get core data and begin showing it, then get additional data and complete 'loading'.
      const _patientDetails = await window.main.axios.get(window.main.api.getPatientDetails.replace("{patient_id}",patient_id));
      if (!_patientDetails || !_patientDetails.data || !Number(_patientDetails.data.id)) {
        throw _patientDetails;
      }
      _stateProps.push({ key: "patientDetails", type: "object", patientDetails: _patientDetails.data });
      // Update all states at once:
      patientViewSetState(_stateProps);
    } catch (err) {
      const errMsg = window.main.lib.parseErrorMessage(err);
      window.main.lib.setStatusState("Error: "+errMsg);
    } finally {
      patientViewSetState(_stateProps);
    }
  }, []);

  // const servicesDataLoad = useCallback(async () => {
  //   const _stateProps = [{key:"patientServicesLoading", type:"boolean", patientServicesLoading:false}];
  //   try {
  //     // Now get additional data
  //     const _openServices = await window.main.axios.get(window.main.api.getPatientServices.replace("{patient_id}",patient_id));
  //     if (!!_openServices && !!_openServices.data ) {
  //       _stateProps.push({ key: "patientServices", type: "array", patientServices: _openServices.data });
  //     }
  //     // Update all states at once:
  //     patientViewSetState(_stateProps);
  //   } catch (err) {
  //     const errMsg = window.main.lib.parseErrorMessage(err);
  //     window.main.lib.setStatusState("Error: "+errMsg);
  //   } finally {
  //     patientViewSetState(_stateProps);
  //   }
  // }, []);

  const visitsDataLoad = useCallback(async () => {
    const _stateProps = [{key:"patientVisitsLoading", type:"boolean", patientVisitsLoading:false}];
    try {
      // Now get additional data
      const _patientVaccs = await window.main.axios.get(window.main.api.getPatientVisits.replace("{patient_id}",patient_id));
      if (!!_patientVaccs && !!_patientVaccs.data ) {
        _stateProps.push({ key: "patientVisits", type: "array", patientVisits: _patientVaccs.data });
      }
      // Update all states at once:
      patientViewSetState(_stateProps);
    } catch (err) {
      const errMsg = window.main.lib.parseErrorMessage(err);
      window.main.lib.setStatusState("Error: "+errMsg);
    } finally {
      patientViewSetState(_stateProps);
    }
  }, []);

  useEffect(() => {
    initialDataLoad();
    // servicesDataLoad();
    visitsDataLoad();
  }, []);  // ⬅ Trigger only once on initial page load.

  if (!patientViewState.patientLoading && !Number(patientViewState.patientDetails.id)) {
    return (
      <View id="patient_details_error" style={{..._theme.tiles.outerContainer,alignItems:"center",paddingTop:50}}>
        <Text style={{..._theme.tiles.fieldRowValue,color:_theme.colors.caution,fontSize:18,lineHeight:22}}>Patient Details failed to load.</Text>
      </View>
    );
  }

  return (
    <>

      {!!Number(patientViewState.patientDetails.id) &&
        <View id="patient_details__sidescreen" style={_theme.tiles.outerContainer}>

          {/* Patient Profile */}
          <PatientProfile patientObj={patientViewState.patientDetails} />


          {/* Patient Memberships */}
          <View id="patient_memberships" style={_theme.tileRowNested}>
            <Text style={_theme.tiles.sectionHeaderTitle}>Memberships</Text>
            {(!patientViewState.patientDetails.memberships || !patientViewState.patientDetails.memberships.length) &&
              <View id="patient_memberships_row" style={_theme.tiles.sectionRow}>
                <Text style={{..._theme.tiles.fieldRowValue,color:_theme.colors.mode.placeholderTextColor,fontStyle:"italic",fontSize:14,lineHeight:18}}>No memberships.</Text>
              </View>
            }
            {!!patientViewState.patientDetails.memberships && !!patientViewState.patientDetails.memberships.length && patientViewState.patientDetails.memberships.map((membershipObj)=>{
              return (
                <View key={"membershipObj_"+membershipObj.id} id={"patient_membership_row_"+membershipObj.id} style={_theme.tiles.sectionRow}>
                  <Text style={{..._theme.tiles.fieldRowLabel,flexGrow:1,flexBasis:"none",flexGrow:0,flexShrink:1,fontSize:16}}>
                    {membershipObj.title}{!!membershipObj.auto_renew && <FontAwesomeIcon icon={faArrowsRotate} style={{color:_theme.colors.mode.textColor5,height:14,width:14,marginLeft:10,paddingTop:3}} />}
                  </Text>
                  <Text style={{..._theme.tiles.fieldRowValue,flexGrow:1,flexDirection:"row",whiteSpace:"nowrap",marginTop:8}}>
                    {new Date(membershipObj.effective_date.replace(/Z$/,"")).toLocaleString("default",{month:"long",year:"numeric"})} - {new Date(membershipObj.expiration_date.replace(/Z$/,"")).toLocaleString("default",{month:"long",year:"numeric"})}
                  </Text>
                  {(!!membershipObj.notes) &&
                    <Text style={{color:_theme.colors.mode.textColor5,fontSize:12,marginTop:8}}>
                      <FontAwesomeIcon icon={faNoteSticky} style={{color:_theme.colors.mode.textColor5,height:14,width:14,marginBottom:-2}} /> {membershipObj.notes}
                    </Text>
                  }
                </View>
              );
            })}
          </View>


          {/* Patient Appointments */}
          <UpcomingAppointments appointments={patientViewState.patientDetails.appointments} />


          {/* Current Vaccinations */}
          <PatientVaccinations patient_id={patientViewState.patientDetails.id} />


          {/* Unbilled Services */}
          {/* <View id="patient_services" style={_theme.tileRowNested}>
            <Text style={_theme.tiles.sectionHeaderTitle}>Open / Unscheduled Services</Text>
            {!!patientViewState.patientServices && !!patientViewState.patientServices.length && patientViewState.patientServices.map((serviceObj)=>{
              const visit_found = ( !Number(serviceObj.visit_id) ? null : patientViewState.patientDetails.visits.find(o=>(o.id===serviceObj.visit_id)) );
              const visit_link = ( !Number(serviceObj.visit_id) ? ()=>{} : ()=>{ openSideScreen("visit", {client_id:patientViewState.patientDetails.client_id, patient_id:patientViewState.patientDetails.id, visit_id:serviceObj.visit_id}); } );
              return (
                <View key={"serviceObj_"+serviceObj.id} id={"patient_service_row_"+serviceObj.id} style={_theme.tiles.sectionRow}>
                  <Text style={{..._theme.tiles.fieldRowLabel,flexGrow:1,flexBasis:"none",flexGrow:0,flexShrink:1,fontSize:16}}>
                    {serviceObj.title}
                  </Text>
                  <Text style={{..._theme.tiles.fieldRowValue,flexDirection:"row",marginTop:8,fontSize:15,fontWeight:"700"}}>
                    Serial number: {serviceObj.serialnumber}
                  </Text>
                  <Text style={{..._theme.tiles.fieldRowValue,flexDirection:"row",whiteSpace:"nowrap",marginTop:5}}>
                    Administered: {window.main.lib.dateFormat(serviceObj.administered_date.replace(/Z$/,""))}
                  </Text>
                  <Text style={{..._theme.tiles.fieldRowValue,flexDirection:"row",whiteSpace:"nowrap",marginTop:5,...(!!serviceObj.is_expired?{color:_theme.colors.caution}:{})}}>
                    Expiration: {window.main.lib.dateFormat(serviceObj.administered_expiration_date.replace(/Z$/,""))}
                  </Text>
                  {!!visit_found &&
                    <Anchor style={{..._theme.tiles.fieldRowValue,flexDirection:"row",marginTop:5,maxWidth:"100%"}} onPress={visit_link}>
                      <Text style={{..._theme.tiles.fieldRowValue,flexFlow:"none",display:"block",whiteSpace:"nowrap",width:"100%",overflow:"hidden",textOverflow:"ellipsis"}}>Visit: {visit_found.title} {visit_found.title}</Text>
                    </Anchor>
                  }
                </View>
              );
            })}
            {((!patientViewState.patientServices || !patientViewState.patientServices.length) && !patientViewState.patientServicesLoading) &&
              <View id="patient_service_row" style={_theme.tiles.sectionRow}>
                <Text style={{..._theme.tiles.fieldRowValue,color:_theme.colors.mode.placeholderTextColor,fontStyle:"italic",fontSize:14,lineHeight:18}}>No open/unscheduled services.</Text>
              </View>
            }
            {!window.main.lib.getLoadingState() && (!patientViewState.patientServices || !patientViewState.patientServices.length) && !!patientViewState.patientServicesLoading &&
              <View id="patient_services_loading" style={{..._theme.tiles.sectionRow,minHeight:50}}>
                <View style={{zIndex:99999,backgroundColor:"rgba(255,255,255,0.7)",top:0,left:0,bottom:0,right:0,position:"absolute"}}>
                  <ActivityIndicator size="large" color={_theme.colors.mode.base} style={{marginTop:10}} />
                </View>
              </View>
            }
          </View> */}


          {/* Patient Prescriptions */}
          <PatientPrescriptions patient_id={patientViewState.patientDetails.id} />


          {/* Current Reminders */}
          <PatientReminders patient_id={patientViewState.patientDetails.id} />


          {/* Patient Visits */}
          <View id="patient_visits" style={{..._theme.tileRowNested,alignItems:"space-between"}}>
            <Text style={_theme.tiles.sectionHeaderTitle}>Visits</Text>
            {!!patientViewState.patientVisits && !!patientViewState.patientVisits.length && patientViewState.patientVisits.map((visitObj)=>{
              const openVisitSideScreen = ()=>{ openSideScreen("visit", {client_id:patientViewState.patientDetails.client_id, patient_id:patientViewState.patientDetails.id, visit_id:visitObj.id, patientObj:patientViewState.patientDetails}); };
              return (
                <Anchor key={"visitObj_"+visitObj.id} id={"patient_visit_row_"+visitObj.id} style={{..._theme.tiles.sectionRow,flexDirection:"row"}} onPress={openVisitSideScreen}>
                  <View style={{..._theme.containerRow,flexGrow:0}}>
                    <FontAwesomeIcon icon={faHeartPulse} style={{color:_theme.colors.mode.baseDark,height:24,width:24,marginTop:-2,marginBottom:-2,marginRight:10}} />
                  </View>
                  <View style={{..._theme.containerColumn,flexShrink:0,color:"inherit"}}>
                    <Text style={{..._theme.tiles.fieldRowLabel,flexBasis:"0%",fontSize:16,color:"inherit"}}>
                      {visitObj.title}
                    </Text>
                    <Text style={{..._theme.tiles.fieldRowValue,whiteSpace:"nowrap",color:"inherit"}}>{window.main.lib.dateFormat(visitObj.created_date)}</Text>
                  </View>
                </Anchor>
              );
            })}
            {((!patientViewState.patientVisits || !patientViewState.patientVisits.length) && !patientViewState.patientVisitsLoading) &&
              <View id="patient_visit_row" style={_theme.tiles.sectionRow}>
                <Text style={{..._theme.tiles.fieldRowValue,color:_theme.colors.mode.placeholderTextColor,fontStyle:"italic",fontSize:14,lineHeight:18}}>No visits.</Text>
              </View>
            }
            {!window.main.lib.getLoadingState() && (!patientViewState.patientVisits || !patientViewState.patientVisits.length) && !!patientViewState.patientVisitsLoading &&
              <View id="patient_visits_loading" style={{..._theme.tiles.sectionRow,minHeight:50}}>
                <View style={{zIndex:99999,backgroundColor:"rgba(255,255,255,0.7)",top:0,left:0,bottom:0,right:0,position:"absolute"}}>
                  <ActivityIndicator size="large" color={_theme.colors.mode.base} style={{marginTop:10}} />
                </View>
              </View>
            }
          </View>


        </View>
      }


      {!window.main.lib.getLoadingState() && !!patientViewState.patientLoading &&
        // <View style={{zIndex:99999, backgroundColor:"rgba(255,255,255,0.7)", top:0, left:0, bottom:0, right:0, position:(window.main.platform_os==="web"?"fixed":"absolute")}}>
        <View style={{zIndex:99999,backgroundColor:"rgba(255,255,255,0.7)",top:0,left:0,bottom:0,right:0,position:"absolute"}}>{/* 'fixed' can't work within the sidescreen overlay. */}
          <ActivityIndicator size="large" color={_theme.colors.mode.base} style={{marginTop:20}} />
        </View>
      }

    </>
  );

};


// Exports

const config = {
  key: "patient",
  title: "Patient Details",
  view: PatientDetails,
  iconSource: "fa",
  icon: faPaw,
}

export default PatientDetails;
export {
  config,
};
